import { useState, useEffect } from 'react'
import {
	FaChrome,
	FaSafari,
	FaFirefox,
	FaEdge,
	FaApple,
	FaWindows,
	FaLinux,
	FaAndroid,
	FaGlobe,
	FaDesktop,
} from 'react-icons/fa6'
import { fetchWithAuth } from '../../utils/apiClient'
import useGlobalTheme from '../../hooks/useGlobalTheme'

const BROWSER_COLORS = {
	chrome: '#4285F4',
	safari: '#1B88CA',
	firefox: '#FF7139',
	edge: '#0078D7',
}

function formatRelativeTime(dateString) {
	if (!dateString) return ''

	const now = new Date()
	const date = new Date(dateString)
	const diffMs = now - date
	const diffMinutes = Math.floor(diffMs / (1000 * 60))
	const diffHours = Math.floor(diffMs / (1000 * 60 * 60))

	if (diffMinutes < 1) return 'Just now'
	if (diffMinutes < 60) return `${diffMinutes}m ago`
	if (diffHours < 24) return `${diffHours}h ago`

	return date.toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	})
}

function formatLoginMethod(method) {
	switch (method) {
		case 'google': return 'Google'
		case 'outlook': return 'Outlook'
		case 'email': return 'Email/Password'
		default: return 'Email/Password'
	}
}

function detectOsKey(osString) {
	const s = (osString || '').toLowerCase()
	if (s.includes('mac') || s.includes('ios')) return 'apple'
	if (s.includes('win')) return 'windows'
	if (s.includes('android')) return 'android'
	if (s.includes('linux') || s.includes('ubuntu') || s.includes('fedora')) return 'linux'
	return 'other'
}

function detectBrowserKey(browserString) {
	const s = (browserString || '').toLowerCase()
	if (s.includes('chrome')) return 'chrome'
	if (s.includes('safari')) return 'safari'
	if (s.includes('firefox')) return 'firefox'
	if (s.includes('edge')) return 'edge'
	return 'other'
}

function BrowserIcon({ browser, className = 'w-4 h-4' }) {
	const theme = useGlobalTheme()
	const isLight = theme === 'light'
	const key = detectBrowserKey(browser)
	const color = BROWSER_COLORS[key]
	const Icon = {
		chrome: FaChrome,
		safari: FaSafari,
		firefox: FaFirefox,
		edge: FaEdge,
	}[key] || FaGlobe

	return (
		<Icon
			className={`${className} shrink-0 ${color ? '' : (isLight ? 'text-gray-500' : 'text-gray-400')}`}
			style={color ? { color } : undefined}
		/>
	)
}

function OsIcon({ os, className = 'w-3.5 h-3.5' }) {
	const theme = useGlobalTheme()
	const isLight = theme === 'light'
	const key = detectOsKey(os)
	const Icon = {
		apple: FaApple,
		windows: FaWindows,
		android: FaAndroid,
		linux: FaLinux,
	}[key] || FaDesktop

	return <Icon className={`${className} shrink-0 ${isLight ? 'text-gray-500' : 'text-gray-400'}`} />
}

function RefreshIcon({ spinning }) {
	return (
		<svg className={`w-3.5 h-3.5 ${spinning ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
			<path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
		</svg>
	)
}

function SessionsSettings() {
	const theme = useGlobalTheme()
	const isLight = theme === 'light'
	const [devices, setDevices] = useState([])
	const [loading, setLoading] = useState(true)
	const [refreshing, setRefreshing] = useState(false)
	const [error, setError] = useState('')
	const [terminatingDeviceId, setTerminatingDeviceId] = useState(null)
	const [confirmingDeviceId, setConfirmingDeviceId] = useState(null)

	useEffect(() => {
		loadDevices()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (!error) return
		const timer = setTimeout(() => setError(''), 15000)
		return () => clearTimeout(timer)
	}, [error])

	const loadDevices = async ({ silent = false } = {}) => {
		try {
			if (silent) setRefreshing(true)
			else setLoading(true)
			const res = await fetchWithAuth('/api/user/devices')
			if (res.ok) {
				const data = await res.json()
				setDevices(data)
			}
		} catch {
			setError('Failed to load sessions')
		} finally {
			setLoading(false)
			setRefreshing(false)
		}
	}

	const handleTerminateSession = async deviceId => {
		setTerminatingDeviceId(deviceId)
		try {
			const res = await fetchWithAuth('/api/user/terminate-session', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ deviceId }),
			})

			if (res.ok) {
				setDevices(prev => prev.filter(d => d.deviceId !== deviceId))
			} else {
				const data = await res.json()
				setError(data.error || 'Failed to terminate session')
			}
		} catch {
			setError('Failed to terminate session')
		} finally {
			setTerminatingDeviceId(null)
			setConfirmingDeviceId(null)
		}
	}

	const currentDevice = devices.find(d => d.isCurrent)
	const otherDevices = devices
		.filter(d => !d.isCurrent)
		.sort((a, b) => new Date(b.lastCheckIn || b.createdAt) - new Date(a.lastCheckIn || a.createdAt))

	return (
		<div>
			<div className="flex items-start justify-between mb-6 gap-4">
				<div>
					<h2 className={`text-2xl font-bold mb-2 ${isLight ? 'text-gray-900' : 'text-white'}`}>Sessions</h2>
				</div>
				<button
					onClick={() => loadDevices({ silent: true })}
					disabled={refreshing || loading}
					className={`flex items-center gap-1.5 text-xs border rounded-lg px-3 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed shrink-0 ${
						isLight
							? 'text-gray-700 hover:text-gray-900 border-gray-200 hover:bg-gray-50'
							: 'text-gray-300 hover:text-white border-gray-600 hover:bg-white/5'
					}`}
				>
					<RefreshIcon spinning={refreshing} />
					Refresh
				</button>
			</div>

			{error && (
				<div className={`rounded-lg p-3 mb-4 text-sm border ${
					isLight
						? 'bg-red-50 text-red-700 border-red-200'
						: 'bg-red-900/30 text-red-300 border-red-800'
				}`}>
					{error}
					<button
						onClick={() => setError('')}
						className={`float-right ${isLight ? 'text-red-600 hover:text-red-700' : 'text-red-400 hover:text-red-300'}`}
					>
						&times;
					</button>
				</div>
			)}

			{loading ? (
				<div className={`text-sm py-8 text-center ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>
					Loading sessions...
				</div>
			) : devices.length === 0 ? (
				<div className={`text-sm py-8 text-center ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>
					No active sessions found.
				</div>
			) : (
				<div className="space-y-6">
					{currentDevice && (
						<div>
							<div className={`text-xs font-medium uppercase tracking-wide mb-2 ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>
								Current Session
							</div>
							<CurrentDeviceRow device={currentDevice} />
						</div>
					)}

					{otherDevices.length > 0 && (
						<div>
							<div className="flex items-center justify-between mb-2">
								<div className={`text-xs font-medium uppercase tracking-wide ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>
									Other Sessions
								</div>
								<div className={`text-xs ${isLight ? 'text-gray-500' : 'text-gray-500'}`}>
									{otherDevices.length} {otherDevices.length === 1 ? 'device' : 'devices'}
								</div>
							</div>
							<DeviceTable
								devices={otherDevices}
								onTerminate={deviceId => setConfirmingDeviceId(deviceId)}
								terminatingDeviceId={terminatingDeviceId}
							/>
						</div>
					)}
				</div>
			)}

			{confirmingDeviceId && (
				<div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-[25vh] z-50">
					<div className={`rounded-lg p-6 max-w-xl w-full mx-4 ${
						isLight
							? 'bg-white ring-1 ring-gray-200 shadow-lg'
							: 'bg-[#15202b] ring-1 ring-white/10 shadow-[0_0_15px_rgba(59,130,246,0.1)]'
					}`}>
						<h3 className={`text-lg font-semibold mb-2 ${isLight ? 'text-gray-900' : 'text-white'}`}>Revoke session</h3>
						<p className={`text-sm mb-6 ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>
							Are you sure? This device will be logged out immediately.
						</p>
						<div className="flex justify-end gap-3">
							<button
								onClick={() => setConfirmingDeviceId(null)}
								disabled={terminatingDeviceId !== null}
								className={`px-4 py-2 text-sm border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${
									isLight
										? 'text-gray-700 hover:text-gray-900 border-gray-200 hover:bg-gray-50'
										: 'text-gray-300 hover:text-white border-gray-600 hover:bg-white/5'
								}`}
							>
								Cancel
							</button>
							<button
								onClick={() => handleTerminateSession(confirmingDeviceId)}
								disabled={terminatingDeviceId !== null}
								className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-lg flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
							>
								{terminatingDeviceId !== null && (
									<svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
										<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
										<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
									</svg>
								)}
								{terminatingDeviceId !== null ? 'Revoking...' : 'Revoke'}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	)
}

function SessionColGroup() {
	return (
		<colgroup>
			<col style={{ width: '17%' }} />
			<col style={{ width: '17%' }} />
			<col style={{ width: '17%' }} />
			<col style={{ width: '12%' }} />
			<col style={{ width: '14%' }} />
			<col style={{ width: '11%' }} />
			<col style={{ width: '12%' }} />
		</colgroup>
	)
}

function CurrentDeviceRow({ device }) {
	const theme = useGlobalTheme()
	const isLight = theme === 'light'
	const cityLabel = device.city && device.city !== 'Unknown' ? device.city : 'Unknown'
	const countryLabel = device.country && device.country !== 'Unknown' ? device.country : ''

	return (
		<div className={`border rounded-lg overflow-hidden ring-1 ring-blue-500/20 ${
			isLight
				? 'border-gray-200'
				: 'border-gray-600 shadow-[0_0_15px_rgba(59,130,246,0.08)]'
		}`}>
			<table className="w-full text-xs table-fixed">
				<SessionColGroup />
				<tbody>
					<tr>
						<td className="px-4 py-5 whitespace-nowrap">
							<div className="flex items-center gap-2">
								<BrowserIcon browser={device.browser} />
								<span className={`text-xs truncate ${isLight ? 'text-gray-900' : 'text-gray-200'}`}>{device.browser || 'Unknown'}</span>
							</div>
						</td>
						<td className="px-4 py-5 whitespace-nowrap">
							<div className="flex items-center gap-1">
								<OsIcon os={device.os} />
								<span className={`text-xs truncate ${isLight ? 'text-gray-700' : 'text-gray-300'}`}>{device.os || 'Unknown'}</span>
							</div>
						</td>
						<td className="px-4 py-5 whitespace-nowrap">
							<span className={`text-xs truncate ${isLight ? 'text-gray-700' : 'text-gray-300'}`}>
								{cityLabel}{countryLabel ? `, ${countryLabel}` : ''}
							</span>
						</td>
						<td className="px-4 py-5 whitespace-nowrap">
							<span className={`text-xs font-mono truncate ${isLight ? 'text-gray-700' : 'text-gray-300'}`}>{device.ipAddress || '—'}</span>
						</td>
						<td className="px-4 py-5 whitespace-nowrap">
							<span className={`text-xs truncate ${isLight ? 'text-gray-700' : 'text-gray-300'}`}>{formatLoginMethod(device.loginMethod)}</span>
						</td>
						<td className="px-4 py-5 whitespace-nowrap">
							<span className={`text-xs truncate ${isLight ? 'text-gray-700' : 'text-gray-300'}`}>Active now</span>
						</td>
						<td className="px-4 py-5 whitespace-nowrap text-right">
							<span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${
								isLight
									? 'bg-teal-500/15 text-teal-700 border-teal-500/30'
									: 'bg-teal-500/15 text-teal-300 border-teal-500/30'
							}`}>
								This device
							</span>
						</td>
					</tr>
				</tbody>
			</table>
		</div>
	)
}

function DeviceTable({ devices, onTerminate, terminatingDeviceId }) {
	const theme = useGlobalTheme()
	const isLight = theme === 'light'
	return (
		<div className={`border rounded-lg overflow-hidden ${isLight ? 'border-gray-200' : 'border-gray-600'}`}>
			<div className="overflow-x-auto">
				<table className="w-full text-xs table-fixed">
					<SessionColGroup />
					<thead>
						<tr className={`border-b ${isLight ? 'bg-gray-50 border-gray-200' : 'bg-white/[0.02] border-gray-700'}`}>
							<th className={`text-left text-[9px] font-medium uppercase tracking-wider px-4 py-1.5 ${isLight ? 'text-gray-500' : 'text-gray-500'}`}>Browser</th>
							<th className={`text-left text-[9px] font-medium uppercase tracking-wider px-4 py-1.5 ${isLight ? 'text-gray-500' : 'text-gray-500'}`}>Operating System</th>
							<th className={`text-left text-[9px] font-medium uppercase tracking-wider px-4 py-1.5 ${isLight ? 'text-gray-500' : 'text-gray-500'}`}>Location</th>
							<th className={`text-left text-[9px] font-medium uppercase tracking-wider px-4 py-1.5 ${isLight ? 'text-gray-500' : 'text-gray-500'}`}>IP Address</th>
							<th className={`text-left text-[9px] font-medium uppercase tracking-wider px-4 py-1.5 ${isLight ? 'text-gray-500' : 'text-gray-500'}`}>Sign-in Method</th>
							<th className={`text-left text-[9px] font-medium uppercase tracking-wider px-4 py-1.5 ${isLight ? 'text-gray-500' : 'text-gray-500'}`}>Last Active</th>
							<th className={`text-right text-[9px] font-medium uppercase tracking-wider px-4 py-1.5 ${isLight ? 'text-gray-500' : 'text-gray-500'}`}>Action</th>
						</tr>
					</thead>
					<tbody>
						{devices.map((device, idx) => (
							<DeviceRow
								key={device.deviceId}
								device={device}
								onTerminate={() => onTerminate(device.deviceId)}
								isTerminating={terminatingDeviceId === device.deviceId}
								isLast={idx === devices.length - 1}
							/>
						))}
					</tbody>
				</table>
			</div>
		</div>
	)
}

function DeviceRow({ device, onTerminate, isTerminating, isLast }) {
	const theme = useGlobalTheme()
	const isLight = theme === 'light'
	const cityLabel = device.city && device.city !== 'Unknown' ? device.city : 'Unknown'
	const countryLabel = device.country && device.country !== 'Unknown' ? device.country : ''

	const borderClass = isLast ? '' : (isLight ? 'border-b border-gray-200' : 'border-b border-gray-700/60')

	return (
		<tr className={borderClass}>
			<td className="px-4 py-3 whitespace-nowrap">
				<div className="flex items-center gap-2">
					<BrowserIcon browser={device.browser} />
					<span className={`text-xs truncate ${isLight ? 'text-gray-900' : 'text-gray-200'}`}>{device.browser || 'Unknown'}</span>
				</div>
			</td>
			<td className="px-4 py-3 whitespace-nowrap">
				<div className="flex items-center gap-0">
					<OsIcon os={device.os} />
					<span className={`text-xs truncate ${isLight ? 'text-gray-700' : 'text-gray-300'}`}>{device.os || 'Unknown'}</span>
				</div>
			</td>
			<td className="px-4 py-3 whitespace-nowrap">
				<span className={`text-xs truncate ${isLight ? 'text-gray-700' : 'text-gray-300'}`}>
					{cityLabel}{countryLabel ? `, ${countryLabel}` : ''}
				</span>
			</td>
			<td className="px-4 py-3 whitespace-nowrap">
				<span className={`text-xs font-mono truncate ${isLight ? 'text-gray-700' : 'text-gray-300'}`}>{device.ipAddress || '—'}</span>
			</td>
			<td className="px-4 py-3 whitespace-nowrap">
				<span className={`text-xs truncate ${isLight ? 'text-gray-700' : 'text-gray-300'}`}>{formatLoginMethod(device.loginMethod)}</span>
			</td>
			<td className="px-4 py-3 whitespace-nowrap">
				<span className={`text-xs truncate ${isLight ? 'text-gray-700' : 'text-gray-300'}`}>{formatRelativeTime(device.lastCheckIn || device.createdAt)}</span>
			</td>
			<td className="px-4 py-3 whitespace-nowrap text-right">
				<button
					onClick={onTerminate}
					disabled={isTerminating}
					className={`text-xs font-medium inline-flex items-center disabled:opacity-50 disabled:cursor-not-allowed border rounded-md px-2.5 py-1 ${
						isLight
							? 'text-red-600 hover:text-red-700 border-red-500/40 hover:border-red-500/60'
							: 'text-red-400 hover:text-red-300 border-red-500/40 hover:border-red-500/60'
					}`}
				>
					{isTerminating && (
						<svg className={`animate-spin mr-1.5 h-3 w-3 ${isLight ? 'text-red-600' : 'text-red-400'}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
							<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
							<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
						</svg>
					)}
					{isTerminating ? 'Revoking...' : 'Revoke'}
				</button>
			</td>
		</tr>
	)
}

export default SessionsSettings
